import React, { useState } from "react";
import { useSelector } from "react-redux";
import Back from "../components/Common/Back";
import APIKit from "../config/APIKit";
import useTheme from "../hooks/useTheme";
import useBackgroundImage from "../hooks/useBackgroundImage";
import { getFullScreenBackgroundStyle } from "../utils/backgroundStyles";
import { FaClipboardList, FaMobileAlt, FaMapMarkerAlt, FaTruck, FaUtensils, FaClock, FaCreditCard } from "react-icons/fa";
import { useTranslation } from "react-i18next";

export default function MyOrders() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const { profile } = useSelector((state) => state.profile);
  const theme = useTheme();
  const backgroundImage = useBackgroundImage();

  const [mobile, setMobile] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedMobile = mobile.trim();
    if (!trimmedMobile) {
      setError(t('my_orders.mobile_placeholder'));
      return;
    }
    setError("");
    setLoading(true);
    setOrders(null);
    try {
      const response = await APIKit.get("order/myOrder", {
        params: {
          store_id: profile?.id,
          number: trimmedMobile,
        },
      });
      const orderData = response?.data ?? response?.orders ?? response;
      setOrders(Array.isArray(orderData) ? orderData : []);
    } catch (err) {
      setOrders([]);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          t('cart.something_went_wrong')
      );
    } finally {
      setLoading(false);
    }
  };

  const orderList = Array.isArray(orders) ? orders : [];
  const hasSearched = orders !== null;

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    const d = new Date(timestamp);
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price) => {
    const n = parseFloat(price);
    return isNaN(n) ? "—" : n % 1 === 0 ? n : n.toFixed(2);
  };

  const formatPaymentMethod = (note) => {
    if (!note) return "—";
    const match = note.match(/Payment:\s*(.+)/i);
    const method = match ? match[1].trim() : note;
    return method.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatDeliveryTime = (time) => {
    if (!time) return null;
    if (typeof time === "string" && /^\d{1,2}:\d{2}/.test(time)) return time;
    const d = new Date(time);
    return isNaN(d.getTime()) ? time : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };

  const getStatusText = (status) => {
    switch (status) {
      case "PENDING": return t('my_orders.pending');
      case "COMPLETED": return t('my_orders.completed');
      case "CANCELLED": return t('my_orders.cancelled');
      default: return status || "—";
    }
  };

  return (
    <main
      className="min-h-screen"
      style={getFullScreenBackgroundStyle(backgroundImage)}
    >
      <div className="max-w-screen-xl pt-20 md:pt-24 lg:pt-32 pb-24 md:pb-20 lg:pb-32 mx-auto px-3 md:px-4 lg:px-6">
        <Back />

        <div className="max-w-2xl mx-auto">
          {/* Mobile number input - always visible */}
          <div
            className="mb-6 md:mb-8 p-4 md:p-6 rounded-2xl shadow-xl border border-white/50 overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${theme.colors.cartIconGradientFrom}12, ${theme.colors.cartIconGradientTo}08)`,
              backdropFilter: "blur(12px)",
            }}
          >
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 md:gap-4 items-stretch sm:items-end">
              <div className="flex-1 flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${theme.colors.primaryColor}25` }}
                >
                  <FaMobileAlt
                    className="w-6 h-6"
                    style={{ color: theme.colors.primaryColor }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 poppins">
                    {t('my_orders.mobile_label')}
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder={t('my_orders.mobile_placeholder')}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-transparent focus:ring-2 focus:outline-none transition-all text-gray-800 placeholder-gray-400"
                    style={{ "--tw-ring-color": theme.colors.primaryColor }}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-xl font-semibold text-white poppins transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] flex-shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${theme.colors.cartIconGradientFrom}, ${theme.colors.cartIconGradientVia}, ${theme.colors.cartIconGradientTo})`,
                }}
              >
                {loading ? t('my_orders.searching') : t('my_orders.view_orders')}
              </button>
            </form>
            {error && (
              <p className="text-red-600 text-sm poppins mt-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                {error}
              </p>
            )}
          </div>

          {/* Orders section */}
          {hasSearched && (
            <>
              {orderList.length === 0 ? (
                <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-xl border border-white/50 p-8 md:p-12 text-center">
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${theme.colors.primaryColor}15` }}
                  >
                    <FaClipboardList
                      className="w-10 h-10"
                      style={{ color: theme.colors.primaryColor }}
                    />
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-800 poppins mb-2">
                    {t('my_orders.no_orders_found')}
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base">
                    {t('my_orders.no_orders_desc')}
                  </p>
                </div>
              ) : (
              <div>
                <h2 className="text-lg font-bold text-gray-800 poppins mb-4">{t('my_orders.title')}</h2>
                <div className="space-y-6">
                {orderList.map((order, index) => {
                  const orderLines = order?.OrderLines ?? [];
                  const modeIcon = order?.mode === "delivery" ? FaTruck : FaUtensils;
                  const ModeIcon = modeIcon;
                  return (
                    <div
                      key={order?.id ?? index}
                      className="bg-white/80 backdrop-blur-md rounded-2xl shadow-xl border border-white/50 overflow-hidden"
                    >
                      {/* Order header */}
                      <div
                        className="px-4 md:px-6 py-4"
                        style={{
                          background: `linear-gradient(135deg, ${theme.colors.cartIconGradientFrom}15, ${theme.colors.cartIconGradientTo}10)`,
                          borderBottom: `2px solid ${theme.colors.primaryColor}30`,
                        }}
                      >
                        {/* Order status - top */}
                        <div className="flex items-center justify-between mb-3">
                          <span
                            className="inline-flex px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide"
                            style={{
                              backgroundColor:
                                order?.order_status === "PENDING"
                                  ? "#fef3c7"
                                  : order?.order_status === "COMPLETED"
                                  ? "#d1fae5"
                                  : order?.order_status === "CANCELLED"
                                  ? "#fee2e2"
                                  : `${theme.colors.primaryColor}25`,
                              color:
                                order?.order_status === "PENDING"
                                  ? "#b45309"
                                  : order?.order_status === "COMPLETED"
                                  ? "#059669"
                                  : order?.order_status === "CANCELLED"
                                  ? "#dc2626"
                                  : theme.colors.primaryColor,
                            }}
                          >
                            {getStatusText(order?.order_status)}
                          </span>
                          <span className="text-sm font-semibold text-gray-600">
                            {t('my_orders.order_no')}{order?.order_no_store ?? order?.order_no ?? index + 1}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                          <span className="inline-flex items-center gap-1.5 text-gray-600">
                            <FaClock className="w-3.5 h-3.5 flex-shrink-0" />
                            {formatDate(order?.timestamp)}
                          </span>
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-xs font-medium capitalize"
                            style={{
                              backgroundColor: `${theme.colors.primaryColor}20`,
                              color: theme.colors.primaryColor,
                            }}
                          >
                            <ModeIcon className="w-3 h-3" />
                            {order?.mode ?? "—"}
                          </span>
                          {order?.expected_delivery_time && (
                            <span className="inline-flex items-center gap-1.5 text-gray-600" title="Expected delivery">
                              <FaTruck className="w-3.5 h-3.5 flex-shrink-0" />
                              {formatDeliveryTime(order.expected_delivery_time)}
                            </span>
                          )}
                          {order?.note && (
                            <span className="inline-flex items-center gap-1.5 text-gray-600">
                              <FaCreditCard className="w-3.5 h-3.5" />
                              {formatPaymentMethod(order.note)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Order items */}
                      <div className="p-4 md:p-6">
                        <div className="space-y-3 mb-4">
                          {orderLines.map((line, lineIndex) => (
                            <div
                              key={line?.id ?? lineIndex}
                              className="flex gap-3 md:gap-4 p-3 rounded-xl bg-gray-50/80 hover:bg-gray-100/80 transition-colors"
                            >
                              <img
                                src={line?.Product?.DocImage?.imageUrl}
                                alt={(isArabic && line?.Product?.name_ar) ? line.Product.name_ar : line?.Product?.name}
                                className="w-14 h-14 md:w-16 md:h-16 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-800 poppins truncate">
                                  {(isArabic && line?.Product?.name_ar) ? line.Product.name_ar : (line?.Product?.name ?? "—")}
                                </p>
                                {line?.ProductVariant?.name && (
                                  <p
                                    className="text-xs font-medium mt-0.5"
                                    style={{ color: theme.colors.primaryColor }}
                                  >
                                    {(isArabic && line?.ProductVariant?.name_ar) ? line.ProductVariant.name_ar : line.ProductVariant.name}
                                  </p>
                                )}
                                {line?.Product?.ProductCategory?.name && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {(isArabic && line?.Product?.ProductCategory?.name_ar) ? line.Product.ProductCategory.name_ar : line.Product.ProductCategory.name}
                                  </p>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="font-bold" style={{ color: theme.colors.primaryColor }}>
                                  {profile?.currency ?? ""} {formatPrice(line?.total)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatPrice(line?.qty)} × {profile?.currency ?? ""} {formatPrice(line?.price)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Delivery address for delivery orders */}
                        {order?.mode === "delivery" && (order?.line1 || order?.line2) && (
                          <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-blue-50/80">
                            <FaMapMarkerAlt className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-gray-700">
                              {order?.line1 && <p>{order.line1}</p>}
                              {order?.line2 && <p>{order.line2}</p>}
                            </div>
                          </div>
                        )}

                        {/* Total */}
                        <div className="flex justify-between items-center pt-4 border-t-2 border-dashed border-gray-200">
                          <span className="font-semibold text-gray-700">{t('order_summary.total')}</span>
                          <span className="text-lg font-bold" style={{ color: theme.colors.primaryColor }}>
                            {profile?.currency ?? ""} {formatPrice(order?.total)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
}
